import { useCallback, useState } from "react";
import { useCreate, useNotify, useRefresh, type RaRecord as AdminRaRecord } from "react-admin";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

export type RazaRequestCreateLagatDialogProps = {
  open: boolean;
  onClose: () => void;
  razaRequestId: string;
  record: AdminRaRecord;
  /** When set, the new receipt is scoped to this raza event (same request). */
  defaultRazaEventId?: string | null;
  /** Booking purpose id on the event; used when `defaultRazaEventId` is set (API aligns receipt purpose). */
  defaultEventPurpose?: string | null;
};

const RazaRequestCreateLagatDialog = ({
  open,
  onClose,
  razaRequestId,
  record,
  defaultRazaEventId = null,
  defaultEventPurpose = null,
}: RazaRequestCreateLagatDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isLoading }] = useCreate();

  const [amount, setAmount] = useState("");
  const [receiptDate, setReceiptDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [paymentMode, setPaymentMode] = useState<"CASH" | "ONLINE" | "CHEQUE">("CASH");
  const [paymentRef, setPaymentRef] = useState("");

  const resetForm = useCallback(() => {
    setAmount("");
    setReceiptDate(new Date().toISOString().slice(0, 10));
    setPaymentMode("CASH");
    setPaymentRef("");
  }, []);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  }, [isLoading, onClose, resetForm]);

  const handleSubmit = useCallback(() => {
    const n = Number.parseInt(String(amount).replace(/,/g, ""), 10);
    if (!Number.isFinite(n) || n < 1) {
      notify("Enter a lagat amount of at least 1", { type: "warning" });
      return;
    }
    const eventScope =
      typeof defaultRazaEventId === "string" && defaultRazaEventId.trim().length > 0;
    const purposeFromEvent =
      typeof defaultEventPurpose === "string" ? defaultEventPurpose.trim() : "";
    const purposeFromRequest = typeof record.purpose === "string" ? record.purpose.trim() : "";
    const purpose = eventScope ? purposeFromEvent || purposeFromRequest : purposeFromRequest;
    if (!purpose) {
      notify(
        eventScope
          ? "This event has no purpose yet; edit the event and set a booking purpose first"
          : "Raza request is missing purpose; edit the request first",
        { type: "error" }
      );
      return;
    }
    const itsNo = typeof record.itsNo === "string" ? record.itsNo.trim() : "";
    const name = typeof record.name === "string" ? record.name.trim() : "";
    if (!itsNo || !name) {
      notify("ITS and name are required on the raza request", { type: "error" });
      return;
    }
    const rd = receiptDate ? new Date(`${receiptDate}T12:00:00`) : new Date();
    const receiptDateIso = Number.isNaN(rd.getTime()) ? new Date().toISOString() : rd.toISOString();

    const eventId =
      typeof defaultRazaEventId === "string" && defaultRazaEventId.trim()
        ? defaultRazaEventId.trim()
        : undefined;

    create(
      "lagatReceipts",
      {
        data: {
          itsNo,
          name,
          purpose,
          amount: n,
          paymentMode,
          paymentRef: paymentMode === "CASH" ? undefined : paymentRef.trim() || undefined,
          receiptDate: receiptDateIso,
          razaRequestId,
          ...(eventId ? { razaEventId: eventId } : {}),
        },
      },
      {
        onSuccess: () => {
          notify("Lagat receipt created and linked", { type: "success" });
          refresh();
          resetForm();
          onClose();
        },
        onError: (err) => {
          const msg =
            err instanceof Error
              ? err.message
              : typeof err === "string"
                ? err
                : "Could not create receipt";
          notify(msg, { type: "error" });
        },
      }
    );
  }, [
    amount,
    create,
    notify,
    onClose,
    paymentMode,
    paymentRef,
    razaRequestId,
    receiptDate,
    record.itsNo,
    record.name,
    record.purpose,
    defaultRazaEventId,
    defaultEventPurpose,
    refresh,
    resetForm,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create lagat receipt</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A new receipt will be created and linked to this raza request. ITS and name come from the
          request.
          {defaultRazaEventId?.trim() ? (
            <>
              {" "}
              Purpose matches the majlis event; the API stores the same booking purpose on the
              receipt and the event.
            </>
          ) : (
            <> Purpose comes from the request.</>
          )}
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Receipt date"
            type="date"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <FormControl>
            <FormLabel>Payment mode</FormLabel>
            <RadioGroup
              row
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as typeof paymentMode)}
            >
              <FormControlLabel value="CASH" control={<Radio />} label="Cash" />
              <FormControlLabel value="ONLINE" control={<Radio />} label="Online" />
              <FormControlLabel value="CHEQUE" control={<Radio />} label="Cheque" />
            </RadioGroup>
          </FormControl>
          {paymentMode !== "CASH" ? (
            <TextField
              label="Payment reference"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              fullWidth
              multiline
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={isLoading}>
          {isLoading ? "Creating…" : "Create and link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RazaRequestCreateLagatDialog;
